import React from 'react';
import { makeStyles, Field, Input, Button, Switch, tokens } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { SearchableDropdown } from '../../../../components/common';
import { FilterBarProps, DEFAULT_FILTERS } from '../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '20px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  filtersRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  bottomRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  clearButton: {
    minWidth: '100px',
  },
});

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFilterChange,
  zones,
  clients,
  shifts,
  loading,
}) => {
  const styles = useStyles();

  const handleFilterUpdate = (key: keyof typeof filters, value: string | boolean) => {
    onFilterChange({
      ...filters,
      [key]: value,
    });
  };

  const handleClearFilters = () => {
    onFilterChange(DEFAULT_FILTERS);
  };

  const hasActiveFilters =
    filters.zone_id !== '' ||
    filters.client_id !== '' ||
    filters.shift_id !== '' ||
    filters.date !== new Date().toISOString().split('T')[0] ||
    filters.show_unassigned !== false;

  return (
    <div className={styles.container}>
      <div className={styles.filtersRow}>
        <Field label="Date">
          <Input
            type="date"
            value={filters.date}
            onChange={e => handleFilterUpdate('date', e.target.value)}
            disabled={loading}
          />
        </Field>

        <Field label="Zone">
          <SearchableDropdown
            placeholder="All Zones"
            options={zones}
            selectedValue={filters.zone_id}
            onSelect={value => handleFilterUpdate('zone_id', value)}
            disabled={loading}
          />
        </Field>

        <Field label="Client">
          <SearchableDropdown
            placeholder="All Clients"
            options={clients}
            selectedValue={filters.client_id}
            onSelect={value => handleFilterUpdate('client_id', value)}
            disabled={loading}
          />
        </Field>

        <Field label="Shift">
          <SearchableDropdown
            placeholder="All Shifts"
            options={shifts}
            selectedValue={filters.shift_id}
            onSelect={value => handleFilterUpdate('shift_id', value)}
            disabled={loading}
          />
        </Field>
      </div>

      <div className={styles.bottomRow}>
        <div className={styles.toggleContainer}>
          <Switch
            checked={filters.show_unassigned}
            onChange={(e, data) => handleFilterUpdate('show_unassigned', data.checked)}
            disabled={loading}
            label="Show Unassigned Sites"
          />
        </div>

        {hasActiveFilters && (
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={handleClearFilters}
            disabled={loading}
            className={styles.clearButton}
          >
            Clear Filters
          </Button>
        )}
      </div>
    </div>
  );
};
